import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { graphQLFetcher } from 'api';
import {
  PackageDestinationOverview,
  PackageError,
  PackageHotelDetail,
  PackageHotelNames,
  PackageHotelOffers,
  PackageHotelSearch,
  PackageMoney,
  PackageOffer,
  PackageOfferVerification,
  PackageReferenceData,
  packageDestinationOverviewQuery,
  packageHotelDetailQuery,
  packageHotelNamesQuery,
  packageHotelOffersQuery,
  packageHotelSearchQuery,
  packageOfferVerifyQuery,
  packageSearchReferenceDataQuery,
  hotelOnlyHotelNamesQuery,
  hotelOnlyHotelOffersQuery,
  hotelOnlyHotelSearchQuery,
  hotelOnlySearchReferenceDataQuery,
} from 'api/queries/packageTour';
import {
  VERIFY_TIMEOUT_MS,
  airportPoint,
  lowestPrice,
  nextToVerify,
  servedHotels,
  verificationOutcome,
} from 'utils/packageHotel';
import { LocationSearchResponse } from 'interfaces/locations';
import { localizeReference } from 'utils/tourNames';
import { hotelNameQuery } from 'utils/packageSearch';

// WHIT-482: package-tour data for the search mask (P1), the destination
// overview (P2), the hotel list (P3) and the hotel page (P4). WHIT-484: the
// mask, the list and the offers come from the hotel-only queries for a
// hotel-only search (`product` 'hotel'); hotel detail and the live check are
// the same for both.

type Product = 'package' | 'hotel';

interface Result<T> {
  data: T | null;
  isLoading: boolean;
  // Set when the request failed or the API answered with an error.
  error: PackageError | null;
}

const failed: PackageError = { code: 'UPSTREAM_ERROR', message: null };

// Answers kept for the rest of the visit, by request: the reference data (it
// changes over hours, and the mask asks for it again every time it opens)
// and hotel content (GIATA master data, the same for every search).
const kept = new Map<string, unknown>();

// Runs `query` whenever `key` changes; `key` null means "not yet" (e.g. the
// search has no dates). A newer request supersedes an older one.
const usePackageQuery = <
  T extends { status: number; error: PackageError | null },
>(
  field: string,
  query: string,
  variables: Record<string, unknown> | null,
  keep = false,
): Result<T> => {
  const vars = variables ? JSON.stringify(variables) : null;
  const key = vars ? `${field}:${vars}` : null;
  // The answer, and the request it answers: while they differ, the current
  // request is still loading.
  const [answer, setAnswer] = useState<{
    key: string;
    data: T | null;
    error: PackageError | null;
  } | null>(null);

  useEffect(() => {
    if (!key || !vars || kept.has(key)) return;
    const controller = new AbortController();
    graphQLFetcher<Record<string, T>>({
      query,
      variables: JSON.parse(vars),
      signal: controller.signal,
    })
      .then(response => {
        // A superseded request's answer must not replace the current one.
        if (controller.signal.aborted) return;
        const data = response[field];
        if (keep && data?.status === 200) kept.set(key, data);
        setAnswer({
          key,
          data: data?.status === 200 ? data : null,
          error: data?.status === 200 ? null : (data?.error ?? failed),
        });
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setAnswer({ key, data: null, error: failed });
      });
    return () => controller.abort();
  }, [field, query, key, vars, keep]);

  if (!key) return { data: null, isLoading: false, error: null };
  if (kept.has(key))
    return { data: kept.get(key) as T, isLoading: false, error: null };
  // While a new request runs, the previous answer stays: the hotel list
  // keeps its filters on screen (and the focus in them) instead of
  // unmounting them on every change.
  if (answer?.key !== key)
    return { data: answer?.data ?? null, isLoading: true, error: null };
  return { data: answer.data, isLoading: false, error: answer.error };
};

// Names in the visitor's language (WHIT-497): the API sends them in English.
export const usePackageReferenceData = (
  locale: string,
  product: Product = 'package',
) => {
  const { t } = useTranslation();
  const answer = usePackageQuery<PackageReferenceData>(
    product === 'hotel'
      ? 'hotelOnlySearchReferenceData'
      : 'packageSearchReferenceData',
    product === 'hotel'
      ? hotelOnlySearchReferenceDataQuery
      : packageSearchReferenceDataQuery,
    { locale },
    true,
  );
  const data = useMemo(
    () => localizeReference(answer.data, t, locale) as PackageReferenceData,
    [answer.data, t, locale],
  );
  return { ...answer, data };
};

export const usePackageDestinationOverview = (
  criteria: Record<string, unknown> | null,
  locale: string,
) =>
  usePackageQuery<PackageDestinationOverview>(
    'packageDestinationOverview',
    packageDestinationOverviewQuery,
    criteria ? { criteria, locale } : null,
  );

export const usePackageHotelSearch = (
  variables: {
    criteria: Record<string, unknown>;
    sort: { by: string; direction: string };
    page: { number: number; size: number };
  } | null,
  locale: string,
  product: Product = 'package',
) =>
  usePackageQuery<PackageHotelSearch>(
    product === 'hotel' ? 'hotelOnlyHotelSearch' : 'packageHotelSearch',
    product === 'hotel' ? hotelOnlyHotelSearchQuery : packageHotelSearchQuery,
    variables ? { ...variables, locale } : null,
  );

// WHIT-503: hotels by name for the destination box, asked for once the
// typing pauses (api-contract.md, operation 15: no provider call behind it).
const HOTEL_NAMES_DEBOUNCE_MS = 200;

export const usePackageHotelNames = (
  text: string,
  locale: string,
  product: Product = 'package',
) => {
  const q = hotelNameQuery(text);
  const [settled, setSettled] = useState(q);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(q), HOTEL_NAMES_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [q]);
  const answer = usePackageQuery<PackageHotelNames>(
    product === 'hotel' ? 'hotelOnlyHotelNames' : 'packageHotelNames',
    product === 'hotel' ? hotelOnlyHotelNamesQuery : packageHotelNamesQuery,
    q && settled === q ? { q, locale } : null,
    true,
  );
  if (!q) return { hotels: [], isLoading: false, error: null };
  // Still typing: the lookup has not been asked yet.
  if (settled !== q) return { hotels: [], isLoading: true, error: null };
  return {
    hotels: answer.isLoading ? [] : answer.data?.hotels || [],
    isLoading: answer.isLoading,
    error: answer.error,
  };
};

export const usePackageHotelDetail = (hotelId: string | null, locale: string) =>
  usePackageQuery<PackageHotelDetail>(
    'packageHotelDetail',
    packageHotelDetailQuery,
    hotelId ? { hotelId, locale } : null,
    true,
  );

export const usePackageHotelOffers = (
  variables: {
    hotelId: string;
    criteria: Record<string, unknown>;
    offerFilters: Record<string, unknown> | null;
    sort: { by: string; direction: string };
    page: { number: number; size: number };
  } | null,
  locale: string,
  product: Product = 'package',
) =>
  usePackageQuery<PackageHotelOffers>(
    product === 'hotel' ? 'hotelOnlyHotelOffers' : 'packageHotelOffers',
    product === 'hotel' ? hotelOnlyHotelOffersQuery : packageHotelOffersQuery,
    variables ? { ...variables, locale } : null,
  );

// The second phase of P4: the listed offers come from a cached search, so
// each is checked with its tour operator before anyone may choose it. A few
// at a time, in list order — every check is a live provider call — and an
// offer keeps its result while the list around it changes (filters, "show
// more").
export const useOfferVerification = (
  offers: PackageOffer[],
  locale: string,
) => {
  // offerId → the outcome (utils/packageHotel). An offer being checked has
  // none yet; those are in `running`, which needs no render of its own (an
  // offer without an outcome already shows as being checked).
  const [states, setStates] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<
    Record<string, PackageOfferVerification>
  >({});
  const running = useRef(new Map<string, AbortController>());

  useEffect(() => {
    const checks = running.current;
    return () => {
      checks.forEach(controller => controller.abort());
      // A remount (Strict Mode) checks them again.
      checks.clear();
    };
  }, []);

  useEffect(() => {
    const busy = Object.fromEntries(
      Array.from(running.current.keys()).map(id => [id, 'verifying']),
    );
    nextToVerify(offers, { ...states, ...busy }).forEach(offerId => {
      const offer = offers.find(candidate => candidate.offerId === offerId);
      const controller = new AbortController();
      running.current.set(offerId, controller);
      // A timeout aborts the request too, but ends as 'failed' and frees the
      // slot; only an unmount's abort leaves the offer without an outcome.
      let timedOut = false;
      const timer = setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, VERIFY_TIMEOUT_MS);
      graphQLFetcher<{ packageOfferVerify: PackageOfferVerification }>({
        query: packageOfferVerifyQuery,
        variables: { offerId, token: offer?.token || null, locale },
        signal: controller.signal,
      })
        .then(response => {
          const answer = response.packageOfferVerify;
          // A failed check that names an enquiry is kept too (WHIT-495).
          if (answer?.status === 200 || answer?.enquiry) {
            setAnswers(current => ({ ...current, [offerId]: answer }));
          }
          return verificationOutcome(answer);
        })
        .catch(() => 'failed')
        .then(outcome => {
          clearTimeout(timer);
          if (controller.signal.aborted && !timedOut) return;
          running.current.delete(offerId);
          setStates(current => ({ ...current, [offerId]: outcome }));
        });
    });
  }, [offers, states, locale]);

  return { states, answers };
};

const airportLocationQuery = `
  query airportLocation($q: String!, $locales: String!) {
    location(q: $q, locales: $locales) {
      locationSearchResult {
        iataCode
        location {
          lat
          lon
        }
      }
    }
  }
`;

// Where each departure airport is, from the location search by its IATA
// code, kept for the visit like the reference data. An airport whose search
// fails or finds no position is null: off the map, not holding it up.
// Shared by the hotel page's route map (WHIT-505 B1) and the hotel list's
// (WHIT-506).
export const useAirportPoints = (
  airports: { id: string; iata: string }[],
  locale: string,
) => {
  const [points, setPoints] = useState<
    Record<string, { lat: number; lon: number } | null>
  >({});
  const airportsKey = JSON.stringify(airports);

  useEffect(() => {
    const controller = new AbortController();
    (JSON.parse(airportsKey) as { id: string; iata: string }[]).forEach(
      ({ id, iata }) => {
        const keptKey = `airportLocation:${iata}`;
        if (kept.has(keptKey)) {
          const point = kept.get(keptKey) as { lat: number; lon: number };
          setPoints(current => ({ ...current, [id]: point }));
          return;
        }
        graphQLFetcher<LocationSearchResponse>({
          query: airportLocationQuery,
          variables: { q: iata, locales: `${locale},en` },
          signal: controller.signal,
        })
          .then(response => {
            const point = airportPoint(
              response.location?.locationSearchResult,
              iata,
            );
            if (point) kept.set(keptKey, point);
            setPoints(current => ({ ...current, [id]: point }));
          })
          // A failed search leaves the airport off the map, not the map
          // waiting for it.
          .catch(() => {
            if (!controller.signal.aborted)
              setPoints(current => ({ ...current, [id]: null }));
          });
      },
    );
    return () => controller.abort();
  }, [airportsKey, locale]);

  return points;
};

// WHIT-506: which hotels each departure airport serves, for the hotel list's
// map: the list's own search, filtered to one airport at a time, cheapest
// first, one page of SERVED_PAGE_SIZE hotels. Null until every airport has
// answered, and null if none could (the map then draws each hotel's
// cheapest-offer airport); an airport whose search failed is left out.
const SERVED_PAGE_SIZE = 50;

export const useAirportServes = (
  airportIds: string[],
  criteria: Record<string, unknown> | null,
  locale: string,
) => {
  const [served, setServed] = useState<{
    key: string;
    value: Record<string, Record<string, PackageMoney | null>> | null;
  }>({ key: '', value: null });
  const key = JSON.stringify([airportIds, criteria, locale]);

  useEffect(() => {
    const [ids, base, lang] = JSON.parse(key) as [
      string[],
      Record<string, unknown> | null,
      string,
    ];
    if (!base || !ids.length) return;
    const controller = new AbortController();
    Promise.all(
      ids.map(id =>
        graphQLFetcher<{ packageHotelSearch: PackageHotelSearch }>({
          query: packageHotelSearchQuery,
          variables: {
            criteria: {
              ...base,
              transport: {
                ...((base.transport as Record<string, unknown>) || {}),
                departureAirportIds: [id],
              },
            },
            sort: { by: 'PRICE', direction: 'ASC' },
            page: { number: 1, size: SERVED_PAGE_SIZE },
            locale: lang,
          },
          signal: controller.signal,
        })
          .then(response => {
            const answer = response.packageHotelSearch;
            return answer?.status === 200
              ? ([id, servedHotels(answer.results)] as const)
              : null;
          })
          .catch(() => null),
      ),
    ).then(entries => {
      if (controller.signal.aborted) return;
      const answered = entries.filter(
        (entry): entry is NonNullable<typeof entry> => entry !== null,
      );
      setServed({
        key,
        value: answered.length ? Object.fromEntries(answered) : null,
      });
    });
    return () => controller.abort();
  }, [key]);

  // Nothing for a newer search until its own answers are in.
  return served.key === key ? served.value : null;
};

// WHIT-505 B1: what the hotel page's route map needs beyond the offers —
// where each departure airport is, and its lowest price for the trip as
// filtered, whichever airport the list itself is narrowed to. One cheapest
// offer per airport (page size 1, by price): the list's own page holds only
// the airports that happen to be cheapest. Positions come from the location
// search by IATA code, kept for the visit like the reference data.
export const useRouteMapData = (
  airports: { id: string; iata: string }[],
  offersVariables: {
    hotelId: string;
    criteria: Record<string, unknown>;
    offerFilters: Record<string, unknown> | null;
  },
  locale: string,
) => {
  const points = useAirportPoints(airports, locale);
  const [prices, setPrices] = useState<{
    key: string;
    values: Record<string, PackageMoney | null>;
  }>({ key: '', values: {} });
  const pricesKey = JSON.stringify([airports, offersVariables, locale]);

  useEffect(() => {
    const [list, variables] = JSON.parse(pricesKey) as [
      { id: string }[],
      typeof offersVariables,
    ];
    const controller = new AbortController();
    Promise.all(
      list.map(({ id }) =>
        graphQLFetcher<{ packageHotelOffers: PackageHotelOffers }>({
          query: packageHotelOffersQuery,
          variables: {
            ...variables,
            offerFilters: {
              ...(variables.offerFilters || {}),
              departureAirportIds: [id],
            },
            sort: { by: 'PRICE', direction: 'ASC' },
            page: { number: 1, size: 1 },
            locale,
          },
          signal: controller.signal,
        })
          .then(response => {
            const answer = response.packageHotelOffers;
            return [
              id,
              answer?.status === 200 ? lowestPrice(answer.offers) : null,
            ] as const;
          })
          .catch(() => [id, null] as const),
      ),
    ).then(entries => {
      if (controller.signal.aborted) return;
      setPrices({ key: pricesKey, values: Object.fromEntries(entries) });
    });
    return () => controller.abort();
  }, [pricesKey, locale]);

  // No prices while the ones for new filters load: an earlier filter's
  // would show as current.
  return { points, prices: prices.key === pricesKey ? prices.values : {} };
};
